import { Alert } from "@gotts.ai/ui/components/Alert";
import { PcbCard } from "@gotts.ai/ui/devtools/components/PcbCard";
import { useCallback, useEffect, useState } from "react";

import { useRequiredTevmContext } from "../context/TevmContext.js";
import {
  downloadState,
  exportState,
  importState,
} from "../persistence/export-import.js";
import {
  createSnapshot,
  deleteSnapshot,
  getSnapshot,
  listSnapshots,
} from "../persistence/idb-store.js";
import { encodeStateToUrl } from "../persistence/url-state.js";

import type { SnapshotEntry } from "../persistence/idb-store.js";

function SnapshotRow({
  snapshot,
  onRestore,
  onDelete,
  isRestoring,
}: {
  snapshot: SnapshotEntry;
  onRestore: (name: string) => void;
  onDelete: (name: string) => void;
  isRestoring: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded border border-[var(--glass-border)] bg-[var(--glass-bg)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-medium text-[var(--color-text)]">
          {snapshot.name}
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          Block {String(snapshot.blockNumber)} &middot;{" "}
          {new Date(snapshot.createdAt).toLocaleString()} &middot;{" "}
          {String(Math.round(snapshot.sizeBytes / 1024))} KB
        </p>
      </div>
      <div className="flex gap-2 self-end sm:self-auto">
        <button
          onClick={() => {
            onRestore(snapshot.name);
          }}
          disabled={isRestoring}
          className="btn-secondary rounded px-3 py-1"
        >
          Restore
        </button>
        <button
          onClick={() => {
            onDelete(snapshot.name);
          }}
          className="btn-danger rounded px-3 py-1"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export function StateManagement() {
  const ctx = useRequiredTevmContext();
  const [snapshots, setSnapshots] = useState<SnapshotEntry[]>([]);
  const [snapshotName, setSnapshotName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load snapshots on mount
  useEffect(() => {
    void listSnapshots().then(setSnapshots);
  }, []);

  const handleSave = useCallback(async () => {
    if (!snapshotName.trim()) return;

    setIsSaving(true);
    setError(null);
    try {
      const state = await ctx.testClient.request({
        method: "tevm_dumpState" as never,
        params: [] as never,
      } as never);

      const blockNumber = await ctx.publicClient.getBlockNumber();

      const entry: SnapshotEntry = {
        name: snapshotName.trim(),
        state,
        deployment: {
          chainId: 31337,
          mode: "from-scratch",
          addresses: {},
        },
        blockNumber: Number(blockNumber),
        createdAt: Date.now(),
        sizeBytes: new Blob([state]).size,
      };

      await createSnapshot(entry);
      setSnapshots(await listSnapshots());
      setSnapshotName("");
      setStatus(`Saved snapshot "${entry.name}"`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  }, [snapshotName, ctx]);

  const handleRestore = useCallback(
    async (name: string) => {
      setIsRestoring(true);
      setError(null);
      try {
        const snap = await getSnapshot(name);
        if (!snap) {
          setError(`Snapshot "${name}" not found`);
          return;
        }

        await ctx.testClient.request({
          method: "tevm_loadState" as never,
          params: [snap.state] as never,
        } as never);

        setStatus(`Restored snapshot "${name}"`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsRestoring(false);
      }
    },
    [ctx],
  );

  const handleDelete = useCallback(async (name: string) => {
    await deleteSnapshot(name);
    setSnapshots(await listSnapshots());
    setStatus(`Deleted snapshot "${name}"`);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      const state = await ctx.testClient.request({
        method: "tevm_dumpState" as never,
        params: [] as never,
      } as never);

      const blockNumber = await ctx.publicClient.getBlockNumber();
      const exported = exportState(state, null, {
        chainId: 31337,
        blockNumber: Number(blockNumber),
        mode: "from-scratch",
      });

      downloadState(exported, `webenv-state-${String(blockNumber)}.json`);
      setStatus("State exported to file");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [ctx]);

  const handleImport = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const imported = await importState(file);

        await ctx.testClient.request({
          method: "tevm_loadState" as never,
          params: [imported.tevmState] as never,
        } as never);

        setStatus(`Imported state from "${file.name}"`);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    };
    input.click();
  }, [ctx]);

  const handleShareUrl = useCallback(async () => {
    try {
      const state = await ctx.testClient.request({
        method: "tevm_dumpState" as never,
        params: [] as never,
      } as never);

      const url = encodeStateToUrl(state, window.location.href);
      if (url) {
        await navigator.clipboard.writeText(url);
        setStatus("Shareable URL copied to clipboard");
      } else {
        setError("State too large for URL encoding");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [ctx]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="type-section-title">State Management</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          Save, restore, export, and share your environment state.
        </p>
      </div>

      {error && (
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      )}

      {status && (
        <Alert variant="success" title="Success">
          {status}
        </Alert>
      )}

      {/* Save snapshot */}
      <PcbCard title="Save Snapshot">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={snapshotName}
            onChange={(e) => {
              setSnapshotName(e.target.value);
            }}
            placeholder="Snapshot name..."
            className="flex-1 rounded border border-[var(--glass-border)] bg-[var(--color-black)] px-3 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-gold)] focus:outline-none"
          />
          <button
            onClick={() => void handleSave()}
            disabled={!snapshotName.trim() || isSaving}
            className="btn-primary rounded px-4 py-1.5"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </PcbCard>

      {/* Snapshots list */}
      {snapshots.length > 0 && (
        <div className="space-y-2">
          <h3 className="section-tag">Snapshots</h3>
          {snapshots.map((snap) => (
            <SnapshotRow
              key={snap.name}
              snapshot={snap}
              onRestore={handleRestore}
              onDelete={(name) => void handleDelete(name)}
              isRestoring={isRestoring}
            />
          ))}
        </div>
      )}

      {/* Export / Import / Share */}
      <PcbCard title="Export & Share">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => void handleExport()}
            className="btn-ghost rounded px-4 py-1.5"
          >
            Export to File
          </button>
          <button
            onClick={() => {
              handleImport();
            }}
            className="btn-ghost rounded px-4 py-1.5"
          >
            Import from File
          </button>
          <button
            onClick={() => void handleShareUrl()}
            className="btn-ghost rounded px-4 py-1.5"
          >
            Copy Share URL
          </button>
        </div>
      </PcbCard>
    </div>
  );
}
